import { useNavigate, useLocation, Link } from 'react-router-dom';
import { X, Plus, Home, Settings } from '@/components/icons';
import { DiagramIcon } from '../diagramIcons';
import { useTabs } from '../../context/TabsContext';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../pages/Auth/AuthProvider';
import { useCreateDiagramMutation, useDiagramsQuery } from '../../hooks/queries';
import { SaveStatusPill } from './SaveStatusPill';

/**
 * Browser-style strip of open diagram tabs (diagrams-only, persisted), with a
 * pinned Home affordance on the left. Present in both the workspace shell and
 * the editor layout for visual coherence.
 */
export function TabStrip() {
  const { tabs, openTab, closeTab, settingsOpen, closeSettingsTab } = useTabs();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const { data: diagrams = [] } = useDiagramsQuery(isAuthenticated);
  const createDiagram = useCreateDiagramMutation();
  const activeId = location.pathname.match(/^\/editor\/([^/]+)$/)?.[1];
  const isHome = location.pathname === '/';
  const isSettings = location.pathname === '/settings';

  // '+' → open a new diagram immediately. Reuse an existing empty 'Untitled
  // Diagram' rather than piling up blanks; otherwise create one and open it.
  const handleNewDiagram = async () => {
    if (createDiagram.isPending) return;
    const existingEmpty = diagrams.find(
      (d) => d.name === 'Untitled Diagram' && (d.nodeCount ?? 0) === 0
    );
    if (existingEmpty) {
      openTab({ id: existingEmpty.id, name: existingEmpty.name, icon: existingEmpty.icon, iconColor: existingEmpty.iconColor });
      navigate(`/editor/${existingEmpty.id}`);
      return;
    }
    try {
      const created = await createDiagram.mutateAsync({ name: 'Untitled Diagram', nodes: [], edges: [] });
      openTab({ id: created.id, name: created.name, icon: created.icon, iconColor: created.iconColor });
      navigate(`/editor/${created.id}`);
    } catch (err) {
      console.error('Failed to create diagram:', err);
      const msg = err instanceof Error ? err.message : '';
      toast.error(
        /limit|cap|403/i.test(msg)
          ? "You've reached your diagram limit. Delete one or upgrade to create more."
          : 'Could not create a new diagram. Please try again.'
      );
    }
  };

  const handleClose = (id: string) => {
    const idx = tabs.findIndex((t) => t.id === id);
    closeTab(id);
    if (id === activeId) {
      const next = tabs[idx + 1] ?? tabs[idx - 1];
      navigate(next ? `/editor/${next.id}` : '/');
    }
  };

  const handleCloseSettings = () => {
    closeSettingsTab();
    if (isSettings) {
      navigate(tabs[0] ? `/editor/${tabs[0].id}` : '/');
    }
  };

  return (
    <div className="flex h-10 flex-none items-end gap-0.5 bg-[var(--tabbar)] px-1.5">
      <Link
        to="/"
        className={`ss-press mr-1 flex h-[31px] items-center gap-2 rounded-md pl-1.5 pr-3 text-[12.5px] font-medium ${
          isHome ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
      >
        <Home className={`h-[18px] w-[18px] ${isHome ? 'text-primary' : ''}`} />
        Home
      </Link>

      {settingsOpen && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate('/settings')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/settings')}
          className={`ss-press group relative flex h-[31px] cursor-pointer items-center gap-2 rounded-t-md px-3 text-[12.5px] font-medium ${
            isSettings
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Settings className={`h-4 w-4 flex-none ${isSettings ? 'text-primary' : ''}`} />
          <span>Settings</span>
          <button
            type="button"
            aria-label="Close Settings"
            onClick={(e) => {
              e.stopPropagation();
              handleCloseSettings();
            }}
            className="grid h-[17px] w-[17px] flex-none place-items-center rounded text-muted-foreground opacity-0 hover:bg-border hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {tabs.map((t) => {
        const isActive = t.id === activeId;
        return (
          <div
            key={t.id}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/editor/${t.id}`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/editor/${t.id}`)}
            className={`ss-press group relative flex h-[31px] max-w-[184px] cursor-pointer items-center gap-2 rounded-t-md px-3 text-[12.5px] font-medium ${
              isActive
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <DiagramIcon name={t.icon} color={t.iconColor} className="h-4 w-4 flex-none" />
            <span className="truncate">{t.name}</span>
            <button
              type="button"
              aria-label={`Close ${t.name}`}
              onClick={(e) => {
                e.stopPropagation();
                handleClose(t.id);
              }}
              className="grid h-[17px] w-[17px] flex-none place-items-center rounded text-muted-foreground opacity-0 hover:bg-border hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}

      <button
        type="button"
        onClick={handleNewDiagram}
        disabled={createDiagram.isPending}
        aria-label="New diagram"
        className="ss-press grid h-[31px] w-8 flex-none place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
      >
        <Plus className="h-[17px] w-[17px]" />
      </button>

      {/* Save state for the open diagram — the editor has no chrome of its own. */}
      <div className="ml-auto flex h-[31px] items-center pr-1">
        <SaveStatusPill />
      </div>
    </div>
  );
}
